import { pageScope } from '../state/store.js';

/**
 * Simple client-side router using History API.
 */
export class Router {
  constructor() {
    this.routes = new Map();
    this.currentRoute = null;
    this.beforeHooks = [];
    this.afterHooks = [];

    window.addEventListener('popstate', () => this.resolve());

    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-link]');
      if (link) {
        e.preventDefault();
        this.navigate(link.getAttribute('href'));
      }
    });
  }

  on(path, handler) {
    // BETA-00176 — warn on duplicate registration. Silent last-wins
    // produced confusing bugs when two pages tried to claim the same
    // route. Console-warn rather than throw so a partial app doesn't
    // refuse to boot, but the developer sees the collision.
    if (this.routes.has(path)) {
      // eslint-disable-next-line no-console
      console.warn('[router] duplicate route registration overwriting handler:', path);
    }
    this.routes.set(path, handler);
    return this;
  }

  before(fn) {
    this.beforeHooks.push(fn);
    return this;
  }

  after(fn) {
    this.afterHooks.push(fn);
    return this;
  }

  navigate(path) {
    if (path === this.currentRoute) return;
    history.pushState(null, '', path);
    this.resolve();
  }

  async resolve() {
    const path = window.location.pathname;

    // BETA-00173 — flush any page-scoped teardowns from the previous
    // route before resolving the next one. This is what makes
    // `store.subscribeForPage` and direct `pageScope.register` calls
    // safe to use without pages remembering to clean up manually.
    pageScope.flush();

    for (const hook of this.beforeHooks) {
      const result = await hook(path);
      if (result === false) return;
    }

    let matched = false;

    for (const [pattern, handler] of this.routes) {
      const params = this._match(pattern, path);
      if (params !== null) {
        this.currentRoute = path;
        await handler(params);
        matched = true;
        break;
      }
    }

    if (!matched) {
      const defaultHandler = this.routes.get('*');
      if (defaultHandler) {
        this.currentRoute = path;
        await defaultHandler({});
      }
    }

    this._updateActiveNav(path);

    for (const hook of this.afterHooks) {
      await hook(path);
    }
  }

  _match(pattern, path) {
    if (pattern === '*') return null;

    const patternParts = pattern.split('/').filter(Boolean);
    const pathParts = path.split('/').filter(Boolean);

    if (patternParts.length !== pathParts.length) return null;

    const params = {};

    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i].startsWith(':')) {
        params[patternParts[i].slice(1)] = decodeURIComponent(pathParts[i]);
      } else if (patternParts[i] !== pathParts[i]) {
        return null;
      }
    }

    return params;
  }

  _updateActiveNav(path) {
    document.querySelectorAll('.nav-item').forEach((item) => {
      const href = item.getAttribute('href') || item.dataset.path;
      if (!href) return;
      const isActive = path === href || (href !== '/' && path.startsWith(href));
      item.classList.toggle('active', isActive);
    });
  }
}

export const router = new Router();
